import React, { memo, useState } from 'react'
import PropTypes from 'prop-types'
import useInsights from '../../../hooks/algolia/useInsights'
import CardFooter from '../../molecules/cardFooter'
import Slider from '../../molecules/CenterCardSlider/CenterCardSlider'
import Modal from './Modal'
import SpecializationChips from './SpecializationChips'
import { NearbyCentersLabel } from './NearbyCentersLabel'
import SegmentCardTracker from './SegmentCardTracker'
import {
  computeAllReviewsCount,
  computeAllReviewsWeightedAverage
} from '../../../domain/centerReviews/centerReviews'
import { createDataLayerIfNotExists } from '../../../utils/global/createDataLayerIfNotExists'
import { CenterCardDescription } from './CenterCardDescription'
import { getCoOccurringDisorderLabel } from '@/domain/primaryFocus/useCases/getCoOccurringDisorderLabel'
import { shouldShowDistanceLabel } from '@/domain/feed/useCases/shouldShowDistanceLabel'
import { getCenterProfileSlug } from '@/domain/center.js'
import { shouldHideCallButton } from '@/domain/feed/useCases/shouldHideCallButton'
import {
  getPhoneNumbers,
  getAccreditation,
  isSamhsa
} from '../../../domain/center'
import CenterCardHighlights from './CenterCardHighlights'
import CenterCardHeader from './CenterCardHeader'
import { isNonEmptyArray } from '../../../utils/checks'
import CenterBadgesAndRatingSection from './CenterBadgesAndRatingSection'
import { FeedMode } from '@/types/models/Term.model'
import { Department } from '@/domain/department/department'
import FavoriteCenterHeartButtonCenterFeed from '@/components/FavoriteCenterHeartButtonCenterFeed/FavoriteCenterHeartButtonCenterFeed'
import { isFavorite } from '@/domain/favoriteCenters/updateFavoriteStorage'
import { parseCenterId } from '@/domain/center/useCases/parseCenterId'

const defaultHighlights = []

const Premium = ({
  slug,
  title,
  highlights = defaultHighlights,
  locationLabel,
  price_label,
  new_specializations,
  highlighted_specializations,
  slides,
  isAd,
  sponsor_tier,
  email,
  businessContact,
  hasCertifications,
  analyticsDashboard,
  rankingInfo,
  showDistanceLabel,
  pageTitle,
  cardIndex,
  activeFacets,
  price,
  listing_template,
  objectID,
  __queryID,
  zoneId,
  pricing,
  accepted_insurance,
  benefitsKey,
  benefitsProvider,
  verify_insurance_link,
  insurance_description,
  providerType,
  currentRefinements,
  center,
  refinementLocation,
  pageType,
  locations,
  department,
  termFeedMode
}) => {
  const slideImages = slides
  highlighted_specializations = highlighted_specializations || []
  const [modal, setModal] = useState(false)
  const { userID, recordAlgoliaClick, recordAlgoliaConversion } = useInsights()

  const displayDistanceLabel = shouldShowDistanceLabel({
    providerType,
    rankingInfo,
    showDistanceLabel,
    centerLocations: locations,
    refinementLocation
  })

  const hideCallButton = shouldHideCallButton({
    pageType,
    currentRefinements
  })

  const handleClick = async () => {
    recordAlgoliaClick({
      objectID: slug,
      queryID: __queryID,
      userID,
      event: 'ListingPageClick',
      position: cardIndex + 1,
      referrer: window.location.pathname
    })

    window.localStorage.setItem('queryID', __queryID)
  }

  const handleDataLayerInquiryClick = () => {
    createDataLayerIfNotExists()
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'Inquiry click',
      ecommerce: {
        items: [
          {
            item_name: title,
            item_id: slug,
            item_variant: listing_template,
            item_category: 'Inquiry Form Modal Click'
          }
        ]
      }
    })
  }

  const toggleModal = () => {
    setModal(!modal)
    if (document) {
      document.querySelector('html').style.overflow = modal ? null : 'hidden'
    }
  }

  // the below code pertains to Google Tag Manager and may have no context outside of this file; DO NOT DELETE.
  const handleCardTitleDataLayerClick = () => {
    createDataLayerIfNotExists()
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'select_item',
      ecommerce: {
        items: [
          {
            item_name: title,
            item_id: slug,
            index: cardIndex + 1,
            item_list_name: 'Browse All Rehabs By Location',
            item_category: 'Card Title Link Click'
          }
        ]
      }
    })
  }

  // the below code pertains to Google Tag Manager and may have no context outside of this file; DO NOT DELETE.
  const handleCardPhoneNumberDataLayerClick = () => {
    createDataLayerIfNotExists()
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        items: [
          {
            item_name: title,
            item_id: slug,
            index: cardIndex + 1,
            item_list_name: 'Browse All Rehabs By Location',
            item_category: 'Feed Card Phone Number Click'
          }
        ]
      }
    })
  }

  const allReviewsCount = computeAllReviewsCount(center)
  const allReviewsWeightedAverage = computeAllReviewsWeightedAverage(center)

  const coOccurringDisorder = getCoOccurringDisorderLabel(center)

  return (
    <>
      {modal && (
        <Modal
          queryID={__queryID}
          dashboard={analyticsDashboard}
          busContact={businessContact}
          slug={slug}
          email={email}
          toggle={toggleModal}
          location={locationLabel}
          title={title}
          trackingID={'Center Feed Card'}
          image={slideImages[0]}
          insuranceAccepted={price?.includes('Insurance Accepted')}
          benefitsKey={benefitsKey}
          benefitsProvider={benefitsProvider}
        />
      )}
      <div
        className="hit !w-full min-w-full max-w-full"
        data-insights-object-id={slug}
        data-insights-position={cardIndex}
      >
        <SegmentCardTracker
          center={{
            title,
            slug,
            sponsor_tier,
            ...center
          }}
          position={cardIndex + 1}
          zone={zoneId}
          refinements={currentRefinements}
          refinementLocation={refinementLocation}
        >
          <div
            className="mt-6 rounded-2xl border border-gray-300 transition-shadow duration-300 contain-intrinsic-size-[auto_700px] 
            hover:shadow-card sm:mt-8 md:contain-intrinsic-size-[auto_500px]"
          >
            <div
              data-insights-object-id={slug}
              data-insights-position={cardIndex}
              className="relative w-full overflow-hidden rounded-[0.95rem] bg-white md:overflow-visible"
            >
              <a
                href={getCenterProfileSlug(center)}
                target="_blank"
                className="algolia-hit-link inherit-text"
                onClick={handleClick}
              >
                <div className="md:pt-4">
                  <Slider
                    slides={slides}
                    slug={slug}
                  />
                </div>
                <div
                  id="premiumCard"
                  onClick={handleCardTitleDataLayerClick}
                  className="inherit-text"
                >
                  <div className="flex w-full flex-col gap-4 p-4">
                    <div className="flex flex-col gap-2">
                      <CenterCardHeader
                        title={title}
                        providerType={providerType}
                        isAd={isAd}
                        hasCertifications={hasCertifications}
                        centerSlug={center.slug}
                        isPremiumCenter={true}
                        center={center}
                      />
                      <div className="absolute right-4 top-4 md:right-4 md:top-5">
                        <div className="relative flex w-full items-center justify-between">
                          <div className="absolute -right-3 -top-3 md:-right-3 md:-top-0">
                            <FavoriteCenterHeartButtonCenterFeed
                              isFavorite={isFavorite(
                                parseCenterId({ pageType, center })
                              )}
                              centerId={parseCenterId({ pageType, center })}
                              centerSlug={center.slug}
                              pageType={pageType}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-3">
                        {displayDistanceLabel && (
                          <NearbyCentersLabel
                            locationLabel={locationLabel}
                            rankingInfo={rankingInfo}
                            pageTitle={pageTitle}
                            refinementLocation={refinementLocation}
                          />
                        )}

                        <CenterBadgesAndRatingSection
                          center={center}
                          accredidation={getAccreditation(center)}
                          price_label={price_label}
                          slug={slug}
                          accepted_insurance={accepted_insurance}
                          verify_insurance_link={verify_insurance_link}
                          insurance_description={insurance_description}
                          price={pricing}
                          currentRefinements={currentRefinements}
                          allReviewsCount={allReviewsCount}
                          allReviewsWeightedAverage={allReviewsWeightedAverage}
                        />
                      </div>
                    </div>

                    <SpecializationChips
                      new_specializations={new_specializations}
                      highlighted_specializations={
                        activeFacets || highlighted_specializations || []
                      }
                      primaryFocus={center?.primaryFocus}
                      coOccurringDisorder={coOccurringDisorder}
                      isPremium={true}
                      termFeedMode={termFeedMode}
                      department={department}
                    />

                    <CenterCardDescription
                      center={center}
                      activeFacets={activeFacets}
                      currentRefinements={currentRefinements}
                      insuranceDescription={insurance_description}
                      slug={slug}
                    />
                    {isNonEmptyArray(highlights) && (
                      <CenterCardHighlights highlights={highlights} />
                    )}
                  </div>
                </div>
              </a>
            </div>
            <CardFooter
              userID={userID}
              objectID={objectID}
              handleCardPhoneNumberDataLayerClick={
                handleCardPhoneNumberDataLayerClick
              }
              handleClick={handleClick}
              recordAlgoliaConversion={recordAlgoliaConversion}
              phoneNumber={getPhoneNumbers(center)[0]}
              slug={slug}
              handleDataLayerInquiryClick={handleDataLayerInquiryClick}
              __queryID={__queryID}
              currentRefinements={currentRefinements}
              hideCallButton={hideCallButton}
            />
          </div>
        </SegmentCardTracker>
      </div>
    </>
  )
}

export default memo(Premium)
